/**
 * Chat V2 API Service
 *
 * Provides API methods for the Vercel AI SDK compatible chat endpoints.
 * Uses the same authentication pattern as the main api.ts service.
 */

import { config } from '@/config'
import { apiService } from '@/service/api'
import type {
  ChatV2Request,
  Conversation,
  UIMessage,
  AgentId,
  ContextType,
} from '../types'
import {
  CHAT_V2_ENDPOINTS,
  VERCEL_AI_STREAM_HEADER,
  VERCEL_AI_STREAM_VERSION,
} from '../constants'
import { getAuthToken } from '../utils/auth'

// =============================================================================
// Types
// =============================================================================

interface ConversationResponse {
  title?: string | null
  id: string
  context_id: string
  context_type: ContextType
  created_at: string
  updated_at: string
  first_message: string
  active_agent: AgentId
}

interface CreateConversationRequest {
  contextId: string
  contextType: ContextType
}

interface CreateConversationResponse {
  conversationId: string
}

// =============================================================================
// Helper Functions
// =============================================================================

/**
 * Build full URL from path
 */
function getUrl(path: string): string {
  const cleanBaseUrl = config.apiBaseUrl.replace(/\/+$/, '')
  const cleanPath = path.replace(/^\/+/, '')
  return `${cleanBaseUrl}/${cleanPath}`
}

/**
 * Handle fetch errors and authentication
 */
async function fetchWithAuth(
  url: string,
  options: RequestInit = {}
): Promise<Response> {
  const token = await getAuthToken()

  const headers: HeadersInit = {
    ...options.headers,
    Authorization: `Bearer ${token}`,
  }

  const response = await fetch(url, {
    ...options,
    headers,
  })

  if (!response.ok) {
    let errorMessage = response.statusText
    try {
      const errorData = await response.json()
      if (errorData.detail) {
        errorMessage = errorData.detail
      }
    } catch {
      // Use statusText if parsing fails
    }
    const error = new Error(errorMessage)
    console.error('[chat-api-v2]', error)
    throw error
  }

  return response
}

// =============================================================================
// Conversation API
// =============================================================================

/**
 * Create a new conversation
 */
export async function createConversation(
  request: CreateConversationRequest
): Promise<CreateConversationResponse> {
  const response = await fetchWithAuth(
    getUrl('/v1/conversations'),
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        context_id: request.contextId,
        context_type: request.contextType,
      }),
    }
  )

  const data = await response.json()
  return { conversationId: data.conversation.id }
}

/**
 * Get a single conversation by ID
 */
export async function getConversation(
  conversationId: string
): Promise<Conversation> {
  const response = await fetchWithAuth(
    getUrl(`/v1/conversations/${conversationId}`)
  )

  const { conversation: data } = await response.json()
  if (!data) throw new Error('Conversation not found')
  return mapConversationResponse(data)
}

/**
 * List conversations for a context
 */
export async function listConversations(
  contextId: string
): Promise<Conversation[]> {
  const response = await fetchWithAuth(
    getUrl(`/v1/conversations/context/${contextId}`)
  )

  const { conversations: data }: { conversations: ConversationResponse[] } = await response.json()
  return data.map(mapConversationResponse)
}

/**
 * Delete a conversation
 */
export async function deleteConversation(
  conversationId: string
): Promise<void> {
  const response = await fetchWithAuth(
    getUrl(`/v1/conversations/${conversationId}`),
    { method: 'DELETE' }
  )
  const { deleted } = await response.json()
  if (deleted !== true) throw new Error('Failed to delete conversation')
}

/**
 * Rename a conversation
 */
export async function renameConversation(
  conversationId: string,
  newName: string
): Promise<void> {
  await fetchWithAuth(
    getUrl(`/v1/conversations/${conversationId}`),
    {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ title: newName }),
    }
  )
}

/**
 * Map API response to Conversation type
 */
function mapConversationResponse(data: ConversationResponse): Conversation {
  return {
    id: data.id,
    contextId: data.context_id,
    contextType: data.context_type,
    createdAt: new Date(data.created_at),
    updatedAt: new Date(data.updated_at),
    // V2 uses firstMessage as its sidebar/search label, including optimistic renames.
    firstMessage: data.title ?? data.first_message,
    activeAgent: data.active_agent,
  }
}

// =============================================================================
// Chat Streaming API
// =============================================================================

/**
 * Get the URL for the chat stream endpoint
 * This is used by the Vercel AI SDK useChat hook
 */
export function getChatStreamUrl(): string {
  return getUrl(CHAT_V2_ENDPOINTS.chat)
}

/**
 * Create a fetch function for use with Vercel AI SDK
 * This wraps the standard fetch with authentication headers
 */
export function createAuthenticatedFetch(): typeof fetch {
  return async (input: RequestInfo | URL, init?: RequestInit) => {
    const token = await getAuthToken()

    const headers: HeadersInit = {
      ...init?.headers,
      Authorization: `Bearer ${token}`,
      [VERCEL_AI_STREAM_HEADER]: VERCEL_AI_STREAM_VERSION,
    }

    return fetch(input, {
      ...init,
      headers,
    })
  }
}

/**
 * Send a chat message and get a streaming response
 * This is the main method for the V2 chat API
 *
 * @param request - The chat request with messages and context
 * @returns Response object with SSE stream
 */
export async function streamChat(
  request: ChatV2Request
): Promise<Response> {
  const token = await getAuthToken()

  const response = await fetch(getUrl(CHAT_V2_ENDPOINTS.chat), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      [VERCEL_AI_STREAM_HEADER]: VERCEL_AI_STREAM_VERSION,
    },
    body: JSON.stringify({
      messages: request.messages,
      agent_id: request.agentId,
      context_id: request.contextId,
      context_type: request.contextType,
      conversation_id: request.conversationId,
    }),
  })

  if (!response.ok) {
    let errorMessage = response.statusText
    try {
      const errorData = await response.json()
      if (errorData.detail) {
        errorMessage = errorData.detail
      }
    } catch {
      // Use statusText if parsing fails
    }
    const error = new Error(errorMessage)
    console.error('[chat-api-v2]', error)
    throw error
  }

  return response
}

// =============================================================================
// Message API (for non-streaming operations)
// =============================================================================

/**
 * Get messages for a conversation
 * Returns messages in UIMessage format
 */
export async function getConversationMessages(
  conversationId: string
): Promise<UIMessage[]> {
  const response = await fetchWithAuth(
    getUrl(`/v1/conversations/${conversationId}/messages`)
  )

  const data = await response.json()
  return data.messages || data
}

/**
 * Add feedback to a message with star rating (1-5)
 */
export async function addMessageFeedback(
  conversationId: string,
  messageId: string,
  feedback: { rating: number; message?: string; screenshot?: string; userQuestion?: string; assistantAnswer?: string }
): Promise<void> {
  await apiService.addMessageFeedback(conversationId, messageId, feedback)
}

/**
 * Remove feedback from a message
 */
export async function removeMessageFeedback(
  conversationId: string,
  messageId: string
): Promise<void> {
  await apiService.removeFeedback(conversationId, messageId)
}

// =============================================================================
// File Download API
// =============================================================================

/**
 * Supported download formats
 */
export type DownloadFormat = 'docx' | 'pdf' | 'txt' | 'md'

/**
 * Download a chat message as a formatted file.
 * Returns a Blob that can be saved to disk.
 */
export async function downloadMessage(
  conversationId: string,
  messageId: string,
  format: DownloadFormat = 'docx'
): Promise<{ blob: Blob; filename: string }> {
  const options: RequestInit = {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ format }),
  }
  const response = await apiService.fetchOrError(
    apiService.getUrl(`v1/conversations/${conversationId}/messages/${messageId}/download`),
    options,
    { timeoutMs: null }
  )

  const blob = await response.blob()

  // Extract filename from Content-Disposition header
  const disposition = response.headers.get('Content-Disposition')
  let filename = `download.${format}`
  if (disposition) {
    const match = disposition.match(/filename="?([^";\n]+)"?/)
    if (match) {
      filename = match[1]
    }
  }

  return { blob, filename }
}

/**
 * Trigger a browser download for a blob.
 */
export function triggerBlobDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)

  // Delay revocation to ensure download starts in all browsers
  setTimeout(() => URL.revokeObjectURL(url), 100)
}

// =============================================================================
// File Upload API
// =============================================================================

/**
 * Response from file upload
 */
interface FileUploadResponse {
  id: string
  url: string
  filename: string
  mediaType: string
  size: number
}

/**
 * Upload a file for use in chat messages
 * Returns the file URL that can be used in message attachments
 */
export async function uploadFile(
  file: File,
  contextId: string
): Promise<FileUploadResponse> {
  const formData = new FormData()
  formData.append('file', file)
  formData.append('context_id', contextId)

  const token = await getAuthToken()

  const response = await fetch(
    getUrl(`${CHAT_V2_ENDPOINTS.conversations}/upload`),
    {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: formData,
    }
  )

  if (!response.ok) {
    let errorMessage = response.statusText
    try {
      const errorData = await response.json()
      if (errorData.detail) {
        errorMessage = errorData.detail
      }
    } catch {
      // Use statusText if parsing fails
    }
    const error = new Error(errorMessage)
    console.error('[chat-api-v2]', error)
    throw error
  }

  const data = await response.json()
  return {
    id: data.id,
    url: data.url,
    filename: data.filename || file.name,
    mediaType: data.media_type || file.type,
    size: data.size || file.size,
  }
}

/**
 * Upload multiple files for use in chat messages
 */
export async function uploadFiles(
  files: File[],
  contextId: string
): Promise<FileUploadResponse[]> {
  const results = await Promise.all(
    files.map((file) => uploadFile(file, contextId))
  )
  return results
}

/**
 * Convert a File to base64 data URL (for local preview/fallback)
 */
export function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result as string)
    reader.onerror = reject
    reader.readAsDataURL(file)
  })
}

// =============================================================================
// Export as service object for consistency with existing api.ts pattern
// =============================================================================

export const chatApiV2 = {
  // Conversations
  createConversation,
  getConversation,
  listConversations,
  deleteConversation,
  renameConversation,

  // Chat streaming
  getChatStreamUrl,
  createAuthenticatedFetch,
  streamChat,

  // Messages
  getConversationMessages,
  addMessageFeedback,
  removeMessageFeedback,

  // File download
  downloadMessage,
  triggerBlobDownload,

  // File upload
  uploadFile,
  uploadFiles,
  fileToBase64,
}

export default chatApiV2
